import React, { useEffect, useState } from 'react';
import { Download, ExternalLink, FileText, Link2 } from 'lucide-react';
import Modal from './common/Modal';
import { paymentsApi, Payment, OutgoingPaymentDocument } from '../api/payments';
import { adminApi, FeeCalculationSnapshot } from '../api/admin';
import { getErrorMessage } from '../api/client';
import { notifyError } from '../utils/notify';
import { formatPublicIdDisplay } from '../utils/formatPublicId';

const PURPOSE_CATEGORY_LABELS: Record<number, string> = {
  1: 'Business expenses',
  2: 'Personal transfer',
  3: 'Investment',
  4: 'Trade payment',
  5: 'Other',
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatPaymentSourceAccount(payment: Payment): string {
  return [
    payment.fromAccountCurrency,
    payment.clientAccountProviderName,
    payment.fromAccountNumber || payment.fromAccountIban,
    payment.clientAccountBankName,
  ].filter(Boolean).join(' · ') || '—';
}

function formatMoney(amount: number, currency: string): string {
  try {
    return amount.toLocaleString('en-US', { style: 'currency', currency, minimumFractionDigits: 2 });
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

function displayCurrency(preferred: string | null | undefined, fallback: string): string {
  const value = (preferred || '').trim();
  if (!value) return fallback;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
    return fallback;
  }
  return value;
}

function confirmationFileName(contentDisposition: unknown, paymentId: string): string {
  const header = String(contentDisposition ?? '');
  const encoded = /filename\*=UTF-8''([^;]+)|filename\*=(?:"?)([^";]+)/i.exec(header);
  const plain = /filename=(?:"([^"]+)"|([^;\s]+))/i.exec(header);
  const value = encoded?.[1] || encoded?.[2] || plain?.[1] || plain?.[2];

  if (value) {
    try {
      return decodeURIComponent(value.trim());
    } catch {
      return value.trim();
    }
  }

  return `XChangeLab_Payment_Confirmation_${paymentId}.pdf`;
}

interface OutgoingPaymentDetailModalProps {
  isOpen: boolean;
  paymentId: string | null;
  onClose: () => void;
  onOpenLedgerLinks: (id: string) => void;
  onRegisterReturn?: (payment: Payment) => void;
}

function OutgoingPaymentDetailModal({
  isOpen,
  paymentId,
  onClose,
  onOpenLedgerLinks,
  onRegisterReturn,
}: OutgoingPaymentDetailModalProps) {
  const [payment, setPayment] = useState<Payment | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [downloadingConfirmation, setDownloadingConfirmation] = useState(false);
  const [showFeeOverride, setShowFeeOverride] = useState(false);
  const [overrideFeeAmount, setOverrideFeeAmount] = useState('');
  const [overrideFeeReason, setOverrideFeeReason] = useState('');
  const [overrideBusy, setOverrideBusy] = useState(false);
  const [feeBreakdown, setFeeBreakdown] = useState<FeeCalculationSnapshot | null>(null);
  const [showFeeBreakdown, setShowFeeBreakdown] = useState(false);

  useEffect(() => {
    if (!isOpen || !paymentId) {
      setPayment(null);
      setError('');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await paymentsApi.getById(paymentId);
        if (!cancelled) setPayment(res.data);
      } catch (err: unknown) {
        if (!cancelled) setError(getErrorMessage(err, 'Failed to load payment'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, paymentId]);

  useEffect(() => {
    if (!isOpen || !paymentId) {
      setFeeBreakdown(null);
      setShowFeeBreakdown(false);
      return;
    }

    let cancelled = false;
    adminApi.getPaymentFeeCalculation('OUTGOING_PAYMENT', paymentId)
      .then((response) => {
        if (!cancelled) setFeeBreakdown(response.data);
      })
      .catch(() => {
        if (!cancelled) setFeeBreakdown(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, paymentId]);

  const documents: OutgoingPaymentDocument[] =
    payment?.documents && Array.isArray(payment.documents) ? payment.documents : [];

  const openDocument = async (documentId: string) => {
    if (!paymentId) return;
    try {
      const linkRes = await paymentsApi.getAdminOutgoingSupportingDocumentOpenLink(paymentId, documentId);
      const presigned = linkRes.data?.presignedUrl;
      if (presigned && presigned.trim().length > 0) {
        window.open(presigned, '_blank', 'noopener,noreferrer');
        return;
      }
      const blobRes = await paymentsApi.downloadAdminOutgoingSupportingDocument(paymentId, documentId);
      const url = URL.createObjectURL(blobRes.data);
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
    } catch (err: unknown) {
      notifyError(err, 'Could not open document');
    }
  };

  const downloadConfirmation = async () => {
    if (!payment) return;

    try {
      setDownloadingConfirmation(true);
      const response = await paymentsApi.downloadConfirmation(payment.id);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = confirmationFileName(response.headers['content-disposition'], payment.id);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      notifyError(err, 'Could not download payment confirmation');
    } finally {
      setDownloadingConfirmation(false);
    }
  };

  const purposeCat =
    payment?.purposeCategory != null && PURPOSE_CATEGORY_LABELS[payment.purposeCategory as number]
      ? PURPOSE_CATEGORY_LABELS[payment.purposeCategory as number]
      : payment?.purposeCategory != null
        ? String(payment.purposeCategory)
        : '—';

  const feeCurrency = payment
    ? displayCurrency(payment.feeCurrencyId, payment.currency)
    : '';
  const providerFeeCurrency = payment
    ? displayCurrency(payment.providerFeeCurrencyId, payment.currency)
    : '';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Outgoing payment details" size="xl">
      {loading && <div className="text-sm text-gray-500 py-6">Loading…</div>}
      {!loading && error && <div className="text-sm text-red-600 py-2">{error}</div>}
      {!loading && !error && payment && (
        <div className="space-y-6 max-h-[75vh] overflow-y-auto pr-1">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Payment ID</p>
              <p className="font-mono text-sm text-gray-900 break-all">{payment.id}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-800">
                {payment.status}
              </span>
              {payment.providerStatus && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800">
                  {payment.providerName || 'Provider'}: {payment.providerStatus}
                </span>
              )}
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                  payment.paymentKind === 'INTERNAL' ? 'bg-purple-100 text-purple-800' : 'bg-slate-100 text-slate-700'
                }`}
              >
                {payment.paymentKind === 'INTERNAL' ? 'Internal' : 'External'}
              </span>
              <button
                type="button"
                onClick={() => {
                  onOpenLedgerLinks(payment.id);
                  onClose();
                }}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-700 text-white hover:bg-gray-800"
              >
                <Link2 className="w-3.5 h-3.5" />
                Ledger links
              </button>
              {onRegisterReturn && payment.paymentKind?.toUpperCase() === 'EXTERNAL' && ['SENT', 'COMPLETED', 'FAILED'].includes(payment.status?.toUpperCase()) && (
                <button
                  type="button"
                  onClick={() => {
                    onRegisterReturn(payment);
                    onClose();
                  }}
                  className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700"
                >
                  Register return
                </button>
              )}
              {payment.paymentKind?.toUpperCase() === 'EXTERNAL' && ['SENT', 'COMPLETED'].includes(payment.status?.toUpperCase()) && (
                <button
                  type="button"
                  onClick={() => void downloadConfirmation()}
                  disabled={downloadingConfirmation}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  <Download className="w-3.5 h-3.5" />
                  {downloadingConfirmation ? 'Downloading…' : 'Download confirmation'}
                </button>
              )}
            </div>
          </div>

          <section className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Amount</p>
              <p className="font-semibold text-gray-900">{formatMoney(payment.amount, payment.currency)}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Fees</p>
              <p className="text-gray-800">
                Preview:{' '}
                {payment.feeAmountPreview != null ? formatMoney(payment.feeAmountPreview, feeCurrency) : '—'}
              </p>
              {payment.paymentMethod === 'SEPA' && payment.amount > 0 && payment.feeAmountFinal != null && (
                <p className="text-xs text-gray-500">
                  Base {formatMoney(payment.amount, payment.currency)}
                </p>
              )}
              <p className="text-gray-800">
                Final:{' '}
                {payment.feeAmountFinal != null ? formatMoney(payment.feeAmountFinal, feeCurrency) : '—'}
              </p>
              {payment.feeCurrencyMode && (
                <p className="text-xs text-gray-500 mt-1">Mode: {payment.feeCurrencyMode}</p>
              )}
              {payment.feeAmountOriginal != null && feeCurrency !== payment.currency && (
                <p className="text-gray-800">
                  Charged from {feeCurrency} account: {formatMoney(payment.feeAmountOriginal, feeCurrency)}
                </p>
              )}
              {feeBreakdown && (
                <>
                  <button
                    type="button"
                    className="mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                    onClick={() => setShowFeeBreakdown((value) => !value)}
                  >
                    {showFeeBreakdown ? 'Hide calculation details' : 'Show calculation details'}
                  </button>
                  {showFeeBreakdown && (
                    <div className="mt-2 rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-700">
                      <p>Percentage: {formatMoney(feeBreakdown.percentagePartAmount, feeBreakdown.currencyId)}</p>
                      <p>Fixed: {formatMoney(feeBreakdown.fixedPartAmount, feeBreakdown.currencyId)}</p>
                      <p>Total: {formatMoney(feeBreakdown.finalAmount, feeBreakdown.currencyId)}</p>
                      {(feeBreakdown.minApplied || feeBreakdown.maxApplied) && (
                        <p>{feeBreakdown.minApplied ? 'Minimum fee applied' : 'Maximum fee applied'}</p>
                      )}
                    </div>
                  )}
                </>
              )}
              {!['PENDING', 'PROCESSING', 'SENT', 'COMPLETED', 'CANCELLED', 'REJECTED', 'FAILED', 'SUBMITTED', 'UNDER_REVIEW', 'APPROVED', 'ON_HOLD'].includes((payment.status || '').toUpperCase()) && (
                <button
                  type="button"
                  className="mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                  onClick={() => {
                    setOverrideFeeAmount(String(payment.feeAmountFinal ?? payment.feeAmountPreview ?? 0));
                    setOverrideFeeReason('');
                    setShowFeeOverride(true);
                  }}
                >
                  Override fee amount
                </button>
              )}
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Provider fee</p>
              <p className="text-gray-800">
                Expected:{' '}
                {payment.providerFeeExpectedAmount != null
                  ? formatMoney(payment.providerFeeExpectedAmount, providerFeeCurrency)
                  : '—'}
              </p>
              <p className="text-gray-800">
                Actual:{' '}
                {payment.providerFeeActualAmount != null
                  ? formatMoney(payment.providerFeeActualAmount, providerFeeCurrency)
                  : 'Not reported'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Status: {payment.providerFeeSettlementStatus || 'Not created'}
              </p>
              {payment.providerFeeCompensationAmount != null && payment.providerFeeCompensationAmount > 0 && (
                <p className="text-xs text-amber-700">
                  Company compensation: {formatMoney(payment.providerFeeCompensationAmount, providerFeeCurrency)}
                </p>
              )}
              {payment.providerFeeExceptionReason && (
                <p className="text-xs text-red-700 mt-1">{payment.providerFeeExceptionReason}</p>
              )}
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Created</p>
              <p className="text-gray-900">{new Date(payment.createdAt).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Submitted / Sent</p>
              <p className="text-gray-900">
                {payment.submittedAt ? new Date(payment.submittedAt).toLocaleString() : '—'}
                {' · '}
                {payment.sentAt ? new Date(payment.sentAt).toLocaleString() : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Provider</p>
              <p className="text-gray-900">{payment.providerName || '—'}</p>
              {payment.providerStatus && (
                <p className="text-xs text-indigo-700 mt-1">Current status: {payment.providerStatus}</p>
              )}
            </div>
          </section>

          <section className="border-t border-gray-100 pt-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">Client &amp; source account</h4>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <div>
                <dt className="text-xs text-gray-500">Display name</dt>
                <dd className="text-gray-900">{payment.clientDisplayName || payment.clientEmail || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">USER ID / email</dt>
                <dd className="text-gray-900 font-mono text-xs">
                  {[formatPublicIdDisplay(payment.clientPublicId), payment.clientEmail].filter(Boolean).join(' · ') || '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Client ID</dt>
                <dd className="text-gray-900 font-mono text-xs break-all">{payment.clientId}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Selected source account</dt>
                <dd className="text-gray-900">
                  {formatPaymentSourceAccount(payment)}
                </dd>
                <dd className="mt-1 text-xs text-gray-500 font-mono break-all">
                  Account ID: {payment.fromAccountId}
                </dd>
                <dt className="mt-2 text-xs text-gray-500">Provider</dt>
                <dd className="text-gray-900">
                  {payment.clientAccountProviderName || payment.providerName || '—'}
                </dd>
              </div>
            </dl>
          </section>

          <section className="border-t border-gray-100 pt-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">Recipient</h4>
            {payment.paymentKind === 'INTERNAL' ? (
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div>
                  <dt className="text-xs text-gray-500">Recipient email</dt>
                  <dd className="text-gray-900">{payment.recipientClientEmail || '—'}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Recipient public ID</dt>
                  <dd className="text-gray-900 font-mono text-xs">{formatPublicIdDisplay(payment.recipientClientPublicId) || '—'}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Recipient client ID</dt>
                  <dd className="text-gray-900 font-mono text-xs break-all">
                    {payment.recipientClientId || '—'}
                  </dd>
                </div>
              </dl>
            ) : (
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div className="sm:col-span-2">
                  <dt className="text-xs text-gray-500">Name</dt>
                  <dd className="text-gray-900 font-medium">{payment.beneficiary?.name || '—'}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Account / IBAN</dt>
                  <dd className="text-gray-900 font-mono text-xs">{payment.beneficiary?.accountNumber || '—'}</dd>
                  {payment.beneficiary?.iban ? (
                    <dd className="text-gray-700 font-mono text-xs">{payment.beneficiary.iban}</dd>
                  ) : null}
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Bank</dt>
                  <dd className="text-gray-900">{payment.beneficiary?.bankName || '—'}</dd>
                  <dd className="text-gray-600 text-xs">SWIFT {payment.beneficiary?.swiftCode || '—'}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Address</dt>
                  <dd className="text-gray-900">
                    {[payment.beneficiary?.address, payment.beneficiary?.zipCode, payment.beneficiary?.city]
                      .filter(Boolean)
                      .join(', ') || '—'}
                  </dd>
                </div>
                {(payment.beneficiary?.correspondentBank ||
                  payment.beneficiary?.correspondentSwift ||
                  payment.beneficiary?.correspondentAccountNumber ||
                  payment.beneficiary?.correspondentCountry) && (
                  <div className="sm:col-span-2 mt-2 pt-2 border-t border-gray-100">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                      Correspondent / intermediary bank
                    </p>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <dt className="text-xs text-gray-500">Bank name</dt>
                        <dd className="text-gray-900">{payment.beneficiary?.correspondentBank || '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500">Country</dt>
                        <dd className="text-gray-900">{payment.beneficiary?.correspondentCountry || '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500">SWIFT</dt>
                        <dd className="text-gray-900 font-mono text-xs">
                          {payment.beneficiary?.correspondentSwift || '—'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500">Account number</dt>
                        <dd className="text-gray-900 font-mono text-xs">
                          {payment.beneficiary?.correspondentAccountNumber || '—'}
                        </dd>
                      </div>
                    </dl>
                  </div>
                )}
              </dl>
            )}
          </section>

          <section className="border-t border-gray-100 pt-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">Purpose &amp; reference</h4>
            <dl className="space-y-1 text-sm">
              <div>
                <dt className="text-xs text-gray-500">Purpose</dt>
                <dd className="text-gray-900">{payment.purpose || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Category</dt>
                <dd className="text-gray-900">{purposeCat}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Payment reference</dt>
                <dd className="text-gray-900 break-words">{payment.reference || '—'}</dd>
              </div>
            </dl>
          </section>

          {(payment.rejectionReason || payment.failReason) && (
            <section className="border border-red-100 bg-red-50 rounded-lg p-3 text-sm">
              {payment.rejectionReason ? (
                <p className="text-red-900">
                  <span className="font-semibold">Rejection:</span> {payment.rejectionReason}
                </p>
              ) : null}
              {payment.failReason ? (
                <p className="text-red-900 mt-1">
                  <span className="font-semibold">Failed:</span> {payment.failReason}
                </p>
              ) : null}
              {payment.failedAt ? (
                <p className="text-xs text-red-700 mt-1">{new Date(payment.failedAt).toLocaleString()}</p>
              ) : null}
            </section>
          )}

          <section className="border-t border-gray-100 pt-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Supporting documents
            </h4>
            {documents.length === 0 ? (
              <p className="text-sm text-gray-500">No attachments</p>
            ) : (
              <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {documents.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate" title={d.fileName}>
                        {d.fileName}
                      </p>
                      <p className="text-xs text-gray-500">
                        {d.documentType} · {formatBytes(d.fileSize)} ·{' '}
                        {d.createdAt ? new Date(d.createdAt).toLocaleString() : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => openDocument(d.id)}
                      className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Open
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}

      {showFeeOverride && payment && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <h4 className="text-base font-semibold text-gray-900 mb-3">Override fee</h4>
            <label className="block text-xs font-medium text-gray-500 mb-1">Currency</label>
            <input
              type="text"
              readOnly
              value={displayCurrency(payment.feeCurrencyId, payment.currency)}
              className="mb-3 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
            />
            <label className="block text-xs font-medium text-gray-500 mb-1">New fee amount</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={overrideFeeAmount}
              onChange={(e) => setOverrideFeeAmount(e.target.value)}
              className="mb-3 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
            <label className="block text-xs font-medium text-gray-500 mb-1">Reason</label>
            <textarea
              value={overrideFeeReason}
              onChange={(e) => setOverrideFeeReason(e.target.value)}
              rows={3}
              className="mb-4 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-100"
                onClick={() => setShowFeeOverride(false)}
                disabled={overrideBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                disabled={overrideBusy || !overrideFeeReason.trim()}
                onClick={async () => {
                  if (!paymentId) return;
                  try {
                    setOverrideBusy(true);
                    await paymentsApi.overrideFee(paymentId, {
                      newFeeAmount: parseFloat(overrideFeeAmount),
                      reason: overrideFeeReason.trim(),
                    });
                    const res = await paymentsApi.getById(paymentId);
                    setPayment(res.data);
                    setShowFeeOverride(false);
                  } catch (err: unknown) {
                    notifyError(err, 'Failed to override fee');
                  } finally {
                    setOverrideBusy(false);
                  }
                }}
              >
                {overrideBusy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default OutgoingPaymentDetailModal;
